import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Container,
  Stack,
  Group,
  Select,
  Text,
  ScrollArea,
  Alert,
  Loader,
  Checkbox,
} from '@mantine/core';
import { useSchemaForm } from '@ayocore/platform/client/hooks/useSchemaForm';
import { CollapsibleSection } from './CollapsibleSection';
import partnerSchemaDefinition, { type Partner } from '@ayocore/erp/crm/schemas/partner.schema';
import type { PartnerRole } from '@ayocore/erp/crm/schemas/partner-role.schema';
import {
  usePartnerRepository,
  useSalesAreaRepository,
  usePartnerRoleRepository,
} from '@ayocore/erp/crm/client/rpc';
import { userRepository } from '@ayocore/platform/identity/client/rpc';
import { CRM_PARTNER_ROLES } from '@ayocore/erp/crm/constants';

/**
 * EditPartnerFormGrasias — форма редактирования партнёра для клиента Грасиас.
 *
 * Отличия от базовой EditPartnerForm:
 * - Обязательные поля: регион (sales_area_id), менеджер (manager_id)
 * - Регион вынесен в основную секцию
 * - Выбор ролей партнёра (чекбоксы) с загрузкой текущих ролей
 * - Сохранение через partnerRepository.updateExtended()
 *
 * Регистрируется через CrmExtensions в grasias/client/bootstrap.ts.
 */
export function EditPartnerFormGrasias({
  partnerId,
  onPartnerUpdated,
}: {
  partnerId: number;
  onPartnerUpdated?: () => void;
}) {
  const partnerRepository = usePartnerRepository();
  const salesAreaRepository = useSalesAreaRepository();
  const partnerRoleRepository = usePartnerRoleRepository();

  type UpdatePayload = Parameters<typeof partnerRepository.updateExtended>[1];

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [managerInitialOption, setManagerInitialOption] = useState<
    { value: string; label: string } | undefined
  >(undefined);

  const [showRequisites, setShowRequisites] = useState(false);
  const [showNotes, setShowNotes] = useState(false);

  const [availableRoles, setAvailableRoles] = useState<PartnerRole[]>([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
  const [rolesError, setRolesError] = useState<string | null>(null);

  // ID роли "Клиент" — определяется при загрузке справочника ролей
  const [customerRoleId, setCustomerRoleId] = useState<number | null>(null);
  // Рефы для корректной работы customValidation (стабильная ссылка, не stale-замыкание)
  const customerRoleIdRef = useRef<number | null>(null);
  const selectedRoleIdsRef = useRef<number[]>([]);

  // Активные не-системные пользователи, отсортированные по русскому алфавиту
  const managerRepo = useMemo(() => userRepository.onlyHumanActive().orderByName('asc'), []);

  const SchemaForm = useSchemaForm(partnerSchemaDefinition.updateSchema, {
    hideDescriptions: true,
    customValidation: values => {
      const errors: Record<string, string> = {};

      if (!(values as Partial<Partner>).sales_area_id) {
        errors.sales_area_id = 'Обязательное поле';
      }

      // При редактировании менеджер не обязателен — поле можно очистить.
      // Для новых партнёров обязательность проверяется в CreatePartnerFormGrasias.

      return errors;
    },
  });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        const [partner, currentRoles, availRoles] = await Promise.all([
          partnerRepository.findById(partnerId),
          partnerRepository.listRoles(partnerId),
          partnerRoleRepository.where('is_active', true).orderBy('name', 'asc').fetch(),
        ]);

        if (!partner) {
          setLoadError('Партнёр не найден');
          return;
        }

        SchemaForm.setInitialData(partner as Partial<Record<string, unknown>>);

        if (partner.notes) setShowNotes(true);
        if (partner.address || partner.director || partner.inn || partner.ogrn)
          setShowRequisites(true);

        setAvailableRoles(availRoles as PartnerRole[]);
        const currentRoleIds = (currentRoles as PartnerRole[]).map(r => r.id);
        setSelectedRoleIds(currentRoleIds);
        selectedRoleIdsRef.current = currentRoleIds;

        const customerRole = (availRoles as PartnerRole[]).find(
          r => r.code === CRM_PARTNER_ROLES.CUSTOMER
        );
        if (customerRole) {
          customerRoleIdRef.current = customerRole.id;
          setCustomerRoleId(customerRole.id);
        }

        if (partner.manager_id) {
          try {
            const manager = await userRepository.findById(partner.manager_id);
            if (manager) {
              setManagerInitialOption({ value: String(manager.id), label: manager.name });
            }
          } catch {
            // не критично — список менеджеров подгрузится из RepositorySelect
          }
        }
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : 'Ошибка загрузки данных');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [partnerId]);

  const handleSubmit = async (values: typeof SchemaForm.values) => {
    if (selectedRoleIds.length === 0) {
      setRolesError('Выберите хотя бы один тип партнёра');
      throw new Error('Выберите хотя бы один тип партнёра');
    }
    setRolesError(null);

    const updated = await partnerRepository.updateExtended(partnerId, {
      ...(values as UpdatePayload),
      role_ids: selectedRoleIds,
    });

    SchemaForm.setSuccess(`Партнёр "${updated.name}" успешно обновлён!`);
    setTimeout(() => onPartnerUpdated?.(), 1500);
  };

  const handleCancel = () => {
    onPartnerUpdated?.();
  };

  if (loading) {
    return (
      <Group justify='center' py='xl'>
        <Loader size='sm' />
      </Group>
    );
  }

  if (loadError) {
    return (
      <Alert color='red' m='sm'>
        {loadError}
      </Alert>
    );
  }

  return (
    <form
      onSubmit={SchemaForm.handleSubmit(handleSubmit)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '75dvh',
        overflow: 'hidden',
      }}
    >
      <ScrollArea style={{ flex: 1 }} scrollbarSize={0}>
        <Container size='lg' px={{ base: 'xs', sm: 'sm' }} py='xs' maw='100%'>
          <Stack gap='xs'>
            <SchemaForm.Messages />

            {/* Основная информация */}
            <Stack gap='xs'>
              <SchemaForm.Field.name
                size='xs'
                label='Короткое название'
                placeholder='Пример: Grasias gift'
              />

              <SchemaForm.Field.full_name
                size='xs'
                label='Полное наименование'
                placeholder='Пример: Имидж Трейдинг/Экспресс Сувенир/ИП Романова'
              />

              <Stack gap={4}>
                <Text
                  size='xs'
                  c='blue'
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => setShowNotes(prev => !prev)}
                >
                  {showNotes ? 'Скрыть примечание к партнёру' : 'Указать примечание к партнёру'}
                </Text>
                {showNotes && <SchemaForm.Field.notes size='xs' />}
              </Stack>

              {/* Статус + Регион */}
              <Group grow>
                <Select
                  size='xs'
                  label='Статус'
                  placeholder='Выберите статус'
                  data={[
                    { value: 'active', label: 'Активный' },
                    { value: 'inactive', label: 'Неактивный' },
                    { value: 'prospect', label: 'Потенциальный' },
                  ]}
                  value={String(SchemaForm.values.status ?? '')}
                  onChange={v => SchemaForm.setFieldValue('status', v ?? '')}
                  error={SchemaForm.errors.status}
                  searchable={false}
                  clearable={false}
                />
                <SchemaForm.Field.sales_area_id
                  size='xs'
                  label='Регион *'
                  repo={salesAreaRepository.filterActive().orderBy('name', 'asc').limit(200)}
                  displayField='name'
                  placeholder='Выберите регион...'
                />
              </Group>

              {/* Менеджер — обязателен только если выбрана роль "Клиент" */}
              <SchemaForm.Field.manager_id
                size='xs'
                label='Менеджер'
                repo={managerRepo}
                displayField='name'
                placeholder='Выберите менеджера...'
                disabled={!(customerRoleId !== null && selectedRoleIds.includes(customerRoleId))}
                initialOption={managerInitialOption}
              />

              {/* Роли партнёра */}
              {availableRoles.length > 0 && (
                <Stack gap={4}>
                  <Text size='xs' fw={500} c='dimmed'>
                    Тип партнёра *
                  </Text>
                  <Group gap='md'>
                    {availableRoles.map(role => (
                      <Checkbox
                        key={role.id}
                        size='xs'
                        label={role.name}
                        checked={selectedRoleIds.includes(role.id)}
                        onChange={e => {
                          const checked = e.currentTarget.checked;
                          setRolesError(null);
                          setSelectedRoleIds(prev => {
                            const next = checked
                              ? [...prev, role.id]
                              : prev.filter(id => id !== role.id);
                            selectedRoleIdsRef.current = next;
                            return next;
                          });
                          // Снимаем роль "Клиент" — менеджер больше не нужен, поле очищаем
                          if (!checked && role.id === customerRoleIdRef.current) {
                            SchemaForm.setFieldValue('manager_id', null);
                          }
                        }}
                      />
                    ))}
                  </Group>
                  {rolesError && (
                    <Text size='xs' c='red'>
                      {rolesError}
                    </Text>
                  )}
                </Stack>
              )}
            </Stack>

            {/* Реквизиты — раскрываются сами, если у партнёра что-то заполнено */}
            <CollapsibleSection
              label='Реквизиты'
              open={showRequisites}
              onToggle={() => setShowRequisites(prev => !prev)}
            >
              <SchemaForm.Field.address size='xs' />
              <SchemaForm.Field.director size='xs' />
              <Group grow>
                <SchemaForm.Field.inn size='xs' />
                <SchemaForm.Field.ogrn size='xs' />
              </Group>
            </CollapsibleSection>
          </Stack>
        </Container>
      </ScrollArea>

      {/* Кнопки */}
      <div
        style={{
          borderTop: '1px solid #e9ecef',
          backgroundColor: 'white',
          padding: '8px 0 0',
          flexShrink: 0,
        }}
      >
        <Group justify='flex-end'>
          <SchemaForm.buttons.cancel onClick={handleCancel} label='Отмена' size='xs' />
          <SchemaForm.buttons.submit label='Сохранить изменения' size='xs' />
        </Group>
      </div>
    </form>
  );
}

export default EditPartnerFormGrasias;
